// Resizes an image File in the browser before upload so we ship a single
// pipeline-optimised JPEG to S3 instead of a 4-12MB iPhone original.
// Falls through unchanged for HEIC/HEIF, non-image files, or any decode
// error — the server-side OptimizePropertyPhotoJob is the safety net.

const DEFAULT_MAX_DIMENSION = 2048
const DEFAULT_QUALITY = 0.85
const HEIC_EXTENSIONS = /\.(heic|heif)$/i

function canBrowserDecode(file) {
  if (HEIC_EXTENSIONS.test(file.name)) return false
  if (!file.type || !file.type.startsWith("image/")) return false
  if (file.type === "image/heic" || file.type === "image/heif") return false
  return typeof createImageBitmap === "function"
}

export async function resizeImageIfNeeded(file, options = {}) {
  const maxDimension = options.maxDimension || DEFAULT_MAX_DIMENSION
  const quality = options.quality ?? DEFAULT_QUALITY

  if (!canBrowserDecode(file)) return file

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" })

    const { width, height } = bitmap
    const longest = Math.max(width, height)

    // Already small enough — close the bitmap and return the original
    // (re-encoding tiny images just wastes quality).
    if (longest <= maxDimension) {
      bitmap.close?.()
      return file
    }

    const scale = maxDimension / longest
    const targetWidth = Math.round(width * scale)
    const targetHeight = Math.round(height * scale)

    const canvas = typeof OffscreenCanvas === "function"
      ? new OffscreenCanvas(targetWidth, targetHeight)
      : Object.assign(document.createElement("canvas"), { width: targetWidth, height: targetHeight })

    const ctx = canvas.getContext("2d")
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight)
    bitmap.close?.()

    const blob = canvas.convertToBlob
      ? await canvas.convertToBlob({ type: "image/jpeg", quality })
      : await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality))

    if (!blob) return file

    const baseName = file.name.replace(/\.[^.]+$/, "")
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() })
  } catch (error) {
    console.warn("Client-side resize failed, uploading original:", error)
    return file
  }
}

window.resizeImageIfNeeded = resizeImageIfNeeded
